import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area
} from "recharts";

import EtatChargesStructure from "../components/EtatChargesStructure";
import FiltreDepenses from "../components/FiltreDepenses";
import pb from "../api/pocketbase";
import { useNavigate } from "react-router-dom";

// Couleurs modernes et professionnelles
const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

// Données d'exemple
const EXEMPLES_PROJETS = [];
const EXEMPLES_DEPENSES_VALIDEES = [];
const EXEMPLES_DEPENSES_VALIDEES_DG = [];
const EXEMPLES_DEPENSES_REJETEES_DG = [];
const EXEMPLES_CHARGES_STRUCTURE = [];

// Fonctions utilitaires (gardées identiques)
function extraireInfosDepense(depense) {
  let rubrique = "-";
  let sousRubrique = "-";
  let fournisseur = "-";
  let justificatif = null;
  let dateDemande = null;

  if (depense.rubriques) {
    let rubriquesData = depense.rubriques;
    
    if (typeof rubriquesData === "string") {
      try {
        rubriquesData = JSON.parse(rubriquesData);
      } catch {
        rubriquesData = [];
      }
    }

    if (Array.isArray(rubriquesData) && rubriquesData.length > 0) {
      rubrique = rubriquesData[0].rubrique || "-";
      sousRubrique = rubriquesData[0].sousRubrique || "-";
      fournisseur = rubriquesData[0].fournisseur || depense.fournisseur || "-";
    }
  }

  dateDemande = depense.dateDemande || depense.created || depense.dateCreation || depense.created_at || null;
  
  if (dateDemande && dateDemande.seconds) {
    dateDemande = new Date(dateDemande.seconds * 1000);
  } else if (dateDemande) {
    dateDemande = new Date(dateDemande);
  }

  if (fournisseur === "-") {
    fournisseur = depense.fournisseur || "-";
  }

  justificatif = depense.justificatif || null;

  return { rubrique, sousRubrique, fournisseur, justificatif, dateDemande };
}

function extraireRubrique(depense) {
  let rubrique = "-";
  let sousRubrique = "-";
  let rubriquesData = depense.rubriques;

  if (typeof rubriquesData === "string") {
    try {
      rubriquesData = JSON.parse(rubriquesData);
    } catch {
      rubriquesData = [];
    }
  }

  if (Array.isArray(rubriquesData) && rubriquesData.length > 0) {
    const r = rubriquesData[0];
    rubrique = r.rubrique || r.nom || "-";
    sousRubrique = r.sousRubrique || "-";
  } else if (typeof rubriquesData === "object" && rubriquesData !== null) {
    rubrique = rubriquesData.rubrique || rubriquesData.nom || "-";
    sousRubrique = rubriquesData.sousRubrique || "-";
  }
  return { rubrique, sousRubrique };
}

// 🆕 FONCTION DE NORMALISATION (identique à ProjetsPage.js)
const normalize = (str) =>
  (str || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// 🆕 FONCTION AMÉLIORÉE - Calcul du réel pour une ligne budgétaire (identique à ProjetsPage.js)
const getReelForLigneBudgetaire = (projet, ligneBudgetaire, depensesDG) => {
  return depensesDG
    .filter(dep => {
      // 1. Récupérer les infos de la dépense
      let depRubrique = dep.rubrique;
      let depSousRubrique = dep.sousRubrique;
      
      if ((!depRubrique || !depSousRubrique) && dep.rubriques) {
        let rubs = dep.rubriques;
        if (typeof rubs === "string") {
          try { rubs = JSON.parse(rubs); } catch { rubs = []; }
        }
        if (Array.isArray(rubs) && rubs.length > 0) {
          depRubrique = depRubrique || rubs[0].rubrique || rubs[0].nom;
          depSousRubrique = depSousRubrique || rubs[0].sousRubrique;
        }
      }

      // 2. Récupérer le nom du projet
      const depProjetNom = dep.projectNom || dep.nomProjet || dep.projetNom || dep.projet || dep.projetId || "";

      // 3. Vérifier les correspondances
      const projetMatch = (projet.id === depProjetNom || normalize(projet.name || projet.nom || projet.title) === normalize(depProjetNom));
      const rubriqueMatch = normalize(ligneBudgetaire.rubrique) === normalize(depRubrique);
      const sousRubriqueMatch = normalize(ligneBudgetaire.sousRubrique) === normalize(depSousRubrique);

      return projetMatch && rubriqueMatch && sousRubriqueMatch;
    })
    .reduce((sum, dep) => sum + Number(dep.montant || dep.reel || 0), 0);
};


// Composant Toast moderne
const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 1500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = message.type === "success" ? "bg-emerald-500" : 
                  message.type === "error" ? "bg-red-500" : 
                  "bg-blue-500";

  return (
    <div className={`fixed top-20 right-6 z-50 ${bgColor} text-white px-6 py-4 rounded-xl shadow-xl flex items-center space-x-3 transform animate-slide-in-right backdrop-blur-sm`}>
      <div className="flex-shrink-0">
        {message.type === "success" && (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
        {message.type === "error" && (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <span className="font-medium">{message.text}</span>
      <button onClick={onClose} className="flex-shrink-0 ml-4 text-white/80 hover:text-white">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

// Composant de carte de statistique
const StatCard = ({ title, value, subtitle, icon, trend, color = "blue" }) => (
  <div className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-${color}-500 group hover:scale-105`}>
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{value}</p>
        {subtitle && (
          <p className="text-sm text-gray-500">{subtitle}</p>
        )}
        {trend && (
          <div className={`flex items-center mt-3 text-sm ${trend.positive ? 'text-emerald-600' : 'text-red-600'}`}>
            <span className="mr-1">{trend.positive ? '↗️' : '↘️'}</span>
            <span className="font-medium">{trend.value}</span>
          </div>
        )}
      </div>
      <div className={`p-4 rounded-2xl bg-gradient-to-br from-${color}-100 to-${color}-200 text-${color}-600 text-2xl group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
    </div>
  </div>
);

// Composant de bouton moderne
const ModernButton = ({ onClick, variant = "primary", size = "md", children, disabled = false, loading = false, className = "" }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none";
  
  const variants = {
    primary: "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl focus:ring-blue-500",
    success: "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl focus:ring-emerald-500",
    warning: "bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white shadow-lg hover:shadow-xl focus:ring-amber-500",
    danger: "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-xl focus:ring-red-500",
    secondary: "bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white shadow-lg hover:shadow-xl focus:ring-gray-500"
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-4 text-base"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
          <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </button>
  );
};

export default function DGDashboard() {
  const navigate = useNavigate();
  // États principaux (gardés identiques)
  const [activeTab, setActiveTab] = useState("dashboard");
  const [projets, setProjets] = useState(EXEMPLES_PROJETS);
  const [depensesValidees, setDepensesValidees] = useState(EXEMPLES_DEPENSES_VALIDEES);
  const [depensesValideesDG, setDepensesValideesDG] = useState(EXEMPLES_DEPENSES_VALIDEES_DG);
  const [depensesRejeteesDG, setDepensesRejeteesDG] = useState(EXEMPLES_DEPENSES_REJETEES_DG);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [depenseToPending, setDepenseToPending] = useState(null);
  const motifRef = useRef();
  const [filtreDateDebut, setFiltreDateDebut] = useState('');
  const [filtreDateFin, setFiltreDateFin] = useState('');
  const [chargesStructure, setChargesStructure] = useState(EXEMPLES_CHARGES_STRUCTURE);
  const [showChargePendingModal, setShowChargePendingModal] = useState(false);
  const [chargeToPending, setChargeToPending] = useState(null);
  const chargePendingMotifRef = useRef();
  const [depenseDescriptionOpen, setDepenseDescriptionOpen] = useState(null);
  const [showJustificatifModal, setShowJustificatifModal] = useState(false);
  const [justificatifUrl, setJustificatifUrl] = useState(null);
  const [historiqueDescriptionOpen, setHistoriqueDescriptionOpen] = useState(null);
  const [openHistory, setOpenHistory] = useState({ depenses: false, charges: false, immo: false });
  const [immobilisationsValideesCG, setImmobilisationsValideesCG] = useState([]);
  const [chargesValideesDG, setChargesValideesDG] = useState([]);
  const [immoValideesDG, setImmoValideesDG] = useState([]);
  const [showImmobilierPendingModal, setShowImmobilierPendingModal] = useState(false);
  const [immobilierToPending, setImmobilierToPending] = useState(null);
  const immobilierPendingMotifRef = useRef();
  const [activeDepenseMeaTab, setActiveDepenseMeaTab] = useState("a_traiter");
  const [depenseMea, setDepenseMea] = useState([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Loading states pour prévenir les doubles clics
  const [validatingDepenseIds, setValidatingDepenseIds] = useState(new Set());
  const [validatingChargeIds, setValidatingChargeIds] = useState(new Set());
  const [validatingImmoIds, setValidatingImmoIds] = useState(new Set());


  // ...existing code...

  useEffect(() => {
    if (activeTab !== "immobilier") return;

    let unsubImmoCG;
    let unsubImmoDG;
    let unsubImmoMEA;
    let unsubImmobilisation;

    // Fonction pour charger toutes les données immobilières
    const fetchAllImmobilierData = async () => {
      try {
        // Immobilisations validées CG (à traiter par DG)
        const valideesCGData = await pb.collection('immoValideesCG').getFullList({
          sort: '-dateValidationCG'
        });
        setImmobilisationsValideesCG(valideesCGData);

        // Immobilisations validées DG (historique)
        const valideesDGData = await pb.collection('immoValideesDG').getFullList({
          sort: '-dateValidationDG'
        });
        setImmoValideesDG(valideesDGData);

      } catch (error) {
        setImmobilisationsValideesCG([]);
        setImmoValideesDG([]);
        console.error("Erreur chargement immobilisations:", error);
      }
    };

    // Fonction de rafraîchissement périodique
    const refreshImmobilierData = () => {
      fetchAllImmobilierData();
    };

    // Charger les données initiales
    fetchAllImmobilierData();

    // Abonnement temps réel pour immoValideesCG (nouvelles demandes)
    const handleImmoCGSubscription = (e) => {
      if (e.action === 'create') {
        setMessage({ type: 'success', text: 'Nouvelle immobilisation à valider !' });
        setImmobilisationsValideesCG(prev => [e.record, ...prev]);
      } else if (e.action === 'update') {
        setImmobilisationsValideesCG(prev => prev.map(item => item.id === e.record.id ? e.record : item));
      } else if (e.action === 'delete') {
        setImmobilisationsValideesCG(prev => prev.filter(item => item.id !== e.record.id));
      }
    };

    // Abonnement temps réel pour immoValideesDG (historique)
    const handleImmoDGSubscription = (e) => {
      if (e.action === 'create') {
        setImmoValideesDG(prev => [e.record, ...prev]);
      } else if (e.action === 'update') {
        setImmoValideesDG(prev => prev.map(item => item.id === e.record.id ? e.record : item));
      } else if (e.action === 'delete') {
        setImmoValideesDG(prev => prev.filter(item => item.id !== e.record.id));
      }
    };

    // Abonnement temps réel pour immomea (mises en attente)
    const handleImmoMEASubscription = (e) => {
      if (e.action === 'create') {
        setMessage({ type: 'info', text: 'Nouvelle immobilisation mise en attente !' });
      } else if (e.action === 'update') {
        // Rafraîchir les données si une mise en attente est modifiée
        fetchAllImmobilierData();
      } else if (e.action === 'delete') {
        // Rafraîchir les données si une mise en attente est supprimée
        fetchAllImmobilierData();
      }
    };

    // Abonnement temps réel pour la collection immobilisation principale
    const handleImmobilisationSubscription = (e) => {
      // Rafraîchir toutes les données immobilières en cas de changement
      fetchAllImmobilierData();
    };

    // Souscriptions temps réel (avec vérification d'authentification)
    if (pb.authStore.isValid) {
      try {
        pb.collection('immoValideesCG').subscribe('*', handleImmoCGSubscription).then(unsub => {
          unsubImmoCG = unsub;
        }).catch(e => console.debug("Sub immoValideesCG skipped:", e?.message));

        pb.collection('immoValideesDG').subscribe('*', handleImmoDGSubscription).then(unsub => {
          unsubImmoDG = unsub;
        }).catch(e => console.debug("Sub immoValideesDG skipped:", e?.message));

        pb.collection('immomea').subscribe('*', handleImmoMEASubscription).then(unsub => {
          unsubImmoMEA = unsub;
        }).catch(e => console.debug("Sub immomea skipped:", e?.message));

        // Souscription à la collection immobilisation principale pour les changements globaux
        pb.collection('immobilisation').subscribe('*', handleImmobilisationSubscription).then(unsub => {
          unsubImmobilisation = unsub;
        }).catch(e => console.debug("Sub immobilisation skipped:", e?.message));
      } catch (err) {
        console.debug("Subscriptions immobilisations skipped:", err?.message);
      }
    }

    // Rafraîchissement périodique toutes les 15 secondes pour une meilleure réactivité
    const interval = setInterval(refreshImmobilierData, 15000);

    // Rafraîchissement immédiat si on revient sur l'onglet
    const handleVisibilityChange = () => {
      if (!document.hidden && activeTab === "immobilier") {
        refreshImmobilierData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (unsubImmoCG) {
        try {
          unsubImmoCG();
        } catch(e) {
          console.error("Erreur désouscription immoValideesCG:", e);
        }
      }
      if (unsubImmoDG) {
        try {
          unsubImmoDG();
        } catch(e) {
          console.error("Erreur désouscription immoValideesDG:", e);
        }
      }
      if (unsubImmoMEA) {
        try {
          unsubImmoMEA();
        } catch(e) {
          console.error("Erreur désouscription immomea:", e);
        }
      }
      if (unsubImmobilisation) {
        try {
          unsubImmobilisation();
        } catch(e) {
          console.error("Erreur désouscription immobilisation:", e);
        }
      }
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeTab]);

// ...existing code...

  // Effet pour la récupération initiale des données
  useEffect(() => {
    async function loadDepenses() {
      try {
        const data = await pb.collection('depensesValideesCG').getFullList({ sort: '-created' });
        setDepensesValidees(data);
      } catch (error) {
        console.error("Erreur lors du chargement des dépenses DG:", error);
      }
    }
    loadDepenses();
  }, []);

  // 🆕 EFFET AMÉLIORÉ - Récupération en temps réel des dépenses DG (identique à ProjetsPage.js)
  useEffect(() => {
    const fetchDepensesDG = async () => {
      try {
        const data = await pb.collection('depensesValideesDG').getFullList({ sort: '-created' });
        setDepensesValideesDG(data);
      } catch (err) {
        console.error("Erreur lors du chargement des dépenses DG:", err);
        setDepensesValideesDG([]);
      }
    };
    fetchDepensesDG();
  }, []);

  // 🆕 MISE À JOUR PÉRIODIQUE (identique à ProjetsPage.js)
  useEffect(() => {
    const interval = setInterval(() => {
      pb.collection('depensesValideesDG').getFullList({ sort: '-created' })
        .then(data => setDepensesValideesDG(data))
        .catch(err => console.error("Erreur refresh dépenses DG:", err));
    }, 30000); // Toutes les 30 secondes

    return () => clearInterval(interval);
  }, []);

  // 🆕 ÉCOUTE DES ÉVÉNEMENTS STORAGE (identique à ProjetsPage.js)
  useEffect(() => {
    const handleRefresh = () => {
      pb.collection('depensesValideesDG').getFullList({ sort: '-created' })
        .then(data => setDepensesValideesDG(data))
        .catch(err => console.error("Erreur refresh storage dépenses DG:", err));
    };

    window.addEventListener('storage', (event) => {
      if (event.key === 'refreshDepensesDG') {
        handleRefresh();
      }
    });

    return () => {
      window.removeEventListener('storage', handleRefresh);
    };
  }, []);

  // Effets pour la récupération de données en temps réel (gardés identiques)
  useEffect(() => {
    if (activeTab !== "charges") return;

    let unsubCG;

    const fetchChargesData = async () => {
      try {
        // Charges à traiter (validées CG)
        const chargesList = await pb.collection('chargesValideesCG').getFullList({ sort: '-created' });
        setChargesStructure(chargesList);

      } catch (e) {
        setChargesStructure([]);
        console.error("Erreur chargement des données de charges:", e);
      }
    };

    fetchChargesData();
    
    if (pb.authStore.isValid) {
      try {
        pb.collection('chargesValideesCG').subscribe('*', fetchChargesData).then(unsub => unsubCG = unsub).catch(err => console.debug('Sub chargesValideesCG skipped:', err?.message));
      } catch (err) {
        console.debug('Sub chargesValideesCG skipped:', err?.message);
      }
    }

    return () => {
      if (unsubCG) unsubCG();
    };
  }, [activeTab]);

  // Effet spécifique pour l'onglet avancements
  useEffect(() => {
    if (activeTab !== "avancements") return;
    
    const fetchProjectsForProgress = async () => {
      try {
        setLoadingProjects(true);
        const projetsList = await pb.collection('projects').getFullList({ 
          sort: '-created',
          expand: 'entries'
        });
        console.log('Projets pour avancements:', projetsList);
        setProjets(projetsList);
        
        // Sélectionner automatiquement le premier projet s'il n'y en a pas de sélectionné
        if (projetsList.length > 0 && !selectedProject) {
          setSelectedProject(projetsList[0]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des projets pour avancements:", error);
        setMessage({ type: "error", text: "❌ Erreur lors du chargement des projets" });
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjectsForProgress();
  }, [activeTab, selectedProject]);

  // Effet pour gérer la sélection automatique du premier projet
  useEffect(() => {
    if (activeTab === "avancements" && projets.length > 0 && !selectedProject) {
      setSelectedProject(projets[0]);
    }
    // Si le projet sélectionné n'existe plus dans la liste, sélectionner le premier
    if (activeTab === "avancements" && selectedProject && projets.length > 0) {
      const projetExiste = projets.find(p => p.id === selectedProject.id);
      if (!projetExiste) {
        setSelectedProject(projets[0]);
      }
    }
  }, [projets, selectedProject, activeTab]);

  // Effet pour la récupération initiale des données en temps réel
  useEffect(() => {
    let unsubProjetsObj, unsubValideesObj, unsubDGObj, unsubRejeteesObj, unsubDepenseMeaObj, unsubChargesDGObj, unsubImmoDGObj;
    let isMounted = true;

    // Fonction pour charger tous les jeux de données en temps réel
    const fetchAll = async () => {
      if (!isMounted) return;

      // Projets
      try {
        const projetsList = await pb.collection('projects').getFullList({ sort: '-created', expand: 'entries' });
        if (isMounted) setProjets(projetsList);
      } catch (e) {
        console.error("Erreur chargement projets:", e);
        if (isMounted) setProjets([]);
      }

      // Dépenses validées CG
      try {
        const depensesValideesList = await pb.collection('depensesValideesCG').getFullList({ sort: '-created' });
        if (isMounted) setDepensesValidees(depensesValideesList);
      } catch (e) {
        console.error("Erreur chargement depensesValideesCG:", e);
        if (isMounted) setDepensesValidees([]);
      }

      // Dépenses validées DG
      try {
        const depensesValideesDGList = await pb.collection('depensesValideesDG').getFullList({ sort: '-created' });
        if (isMounted) setDepensesValideesDG(depensesValideesDGList);
      } catch (e) {
        console.error("Erreur chargement depensesValideesDG:", e);
        if (isMounted) setDepensesValideesDG([]);
      }

      // Dépenses rejetées DG
      try {
        const depensesRejeteesDGList = await pb.collection('depensesRejeteesDG').getFullList({ sort: '-created' });
        if (isMounted) setDepensesRejeteesDG(depensesRejeteesDGList);
      } catch (e) {
        console.error("Erreur chargement depensesRejeteesDG:", e);
        if (isMounted) setDepensesRejeteesDG([]);
      }

      // Charges validées DG
      try {
        const chargesValideesDGList = await pb.collection('chargesValideesDG').getFullList({ sort: '-created' });
        if (isMounted) setChargesValideesDG(chargesValideesDGList);
      } catch (e) {
        console.error("Erreur chargement chargesValideesDG:", e);
        if (isMounted) setChargesValideesDG([]);
      }

      // Immo validées DG
      try {
        const immoValideesDGList = await pb.collection('immoValideesDG').getFullList({ sort: '-created' });
        if (isMounted) setImmoValideesDG(immoValideesDGList);
      } catch (e) {
        console.error("Erreur chargement immoValideesDG:", e);
        if (isMounted) setImmoValideesDG([]);
      }

      // Dépenses en attente DG
      try {
        const depenseMeaList = await pb.collection('depensemea').getFullList({ filter: 'meaPar = "DG"', sort: '-datemea' });
        if (isMounted) setDepenseMea(depenseMeaList);
      } catch (e) {
        console.error("Erreur chargement depensemea:", e);
        if (isMounted) setDepenseMea([]);
      }
    };

    // Fonction spécifique pour les projets avec gestion d'erreur
    const fetchProjets = async () => {
      try {
        const projetsList = await pb.collection('projects').getFullList({ 
          sort: '-created',
          expand: 'entries'
        });
        if (isMounted) {
          console.log('Projets mis à jour en temps réel:', projetsList);
          setProjets(projetsList);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des projets:', e);
      }
    };

    fetchAll();

    // Abonnements temps réel - séparés pour une meilleure gestion (avec vérification d'authentification)
    if (pb.authStore.isValid) {
      try {
        pb.collection('projects').subscribe('*', fetchProjets).then(unsub => {
          unsubProjetsObj = unsub;
        }).catch(e => console.debug('Sub projets skipped:', e?.message));

        pb.collection('depensesValideesCG').subscribe('*', fetchAll).then(unsub => {
          unsubValideesObj = unsub;
        }).catch(e => console.debug('Sub validées CG skipped:', e?.message));

        pb.collection('depensesValideesDG').subscribe('*', fetchAll).then(unsub => {
          unsubDGObj = unsub;
        }).catch(e => console.debug('Sub validées DG skipped:', e?.message));

        pb.collection('depensesRejeteesDG').subscribe('*', fetchAll).then(unsub => {
          unsubRejeteesObj = unsub;
        }).catch(e => console.debug('Sub rejetées DG skipped:', e?.message));

        pb.collection('depensemea').subscribe('*', fetchAll).then(unsub => {
          unsubDepenseMeaObj = unsub;
        }).catch(e => console.debug('Sub dépenses en attente skipped:', e?.message));

        pb.collection('chargesValideesDG').subscribe('*', fetchAll).then(unsub => {
          unsubChargesDGObj = unsub;
        }).catch(e => console.debug('Sub charges DG skipped:', e?.message));

        pb.collection('immoValideesDG').subscribe('*', fetchAll).then(unsub => {
          unsubImmoDGObj = unsub;
        }).catch(e => console.debug('Sub immo DG skipped:', e?.message));
      } catch (err) {
        console.debug('Subscriptions skipped:', err?.message);
      }
    }

    return () => {
      isMounted = false;
      if (unsubProjetsObj) {
        try {
          unsubProjetsObj();
        } catch (e) {
          console.error('Erreur désouscription projets:', e);
        }
      }
      if (unsubValideesObj) {
        try {
          unsubValideesObj();
        } catch (e) {
          console.error('Erreur désouscription validées:', e);
        }
      }
      if (unsubDGObj) {
        try {
          unsubDGObj();
        } catch (e) {
          console.error('Erreur désouscription DG:', e);
        }
      }
      if (unsubRejeteesObj) {
        try {
          unsubRejeteesObj();
        } catch (e) {
          console.error('Erreur désouscription rejetées:', e);
        }
      }
      if (unsubDepenseMeaObj) {
        try {
          unsubDepenseMeaObj();
        } catch (e) {
          console.error('Erreur désouscription dépenses en attente:', e);
        }
      }
      if (unsubChargesDGObj) {
        try {
          unsubChargesDGObj();
        } catch (e) {
          console.error('Erreur désouscription charges DG:', e);
        }
      }
      if (unsubImmoDGObj) {
        try {
          unsubImmoDGObj();
        } catch (e) {
          console.error('Erreur désouscription immo DG:', e);
        }
      }
    };
  }, []);

  // Effet spécifique pour les dépenses en attente
  useEffect(() => {
    if (activeTab !== "depensemea") return;

    let unsubDepenseMea;
    
    const fetchDepenseMeaData = async () => {
      try {
        const depenseMeaData = await pb.collection('depensemea').getFullList({
          filter: 'meaPar = "DG"',
          sort: '-datemea'
        });
        setDepenseMea(depenseMeaData);
        console.log('Dépenses en attente chargées:', depenseMeaData);
      } catch (error) {
        setDepenseMea([]);
        console.error("Erreur chargement des dépenses en attente:", error);
      }
    };

    fetchDepenseMeaData();
    
    // Abonnement temps réel pour les dépenses en attente (avec vérification d'authentification)
    if (pb.authStore.isValid) {
      try {
        pb.collection('depensemea').subscribe('*', fetchDepenseMeaData).then(unsub => {
          unsubDepenseMea = unsub;
        }).catch(e => console.debug('Sub depensemea skipped:', e?.message));
      } catch (err) {
        console.debug('Sub depensemea skipped:', err?.message);
      }
    }

    return () => {
      if (unsubDepenseMea) {
        try {
          unsubDepenseMea();
        } catch (e) {
          console.debug('Erreur désouscription dépenses en attente:', e);
        }
      }
    };
  }, [activeTab]);

  // Fonctions utilitaires (gardées identiques)
  const getPendingExpenses = () => {
    return depensesValidees.filter(dep => {
      const notValidated = !depensesValideesDG.some(validDep => 
        validDep.expenseId === dep.id || 
        validDep.id === dep.id
      );
      const notRejected = !depensesRejeteesDG.some(rejDep => 
        rejDep.expenseId === dep.id || 
        rejDep.id === dep.id
      );
      return notValidated && notRejected;
    });
  };

  const getValidatedExpenses = () => {
    const processedIds = new Set();
    return depensesValideesDG.filter(depense => {
      const id = depense.expenseId || depense.id;
      if (processedIds.has(id)) {
        return false;
      }
      processedIds.add(id);
      return true;
    });
  };

  // 🆕 FONCTIONS AMÉLIORÉES pour calculer l'avancement des projets (utilisant la logique de ProjetsPage.js)
  const getProjectProgress = (projet) => {
    if (!projet) {
      return { budgetTotal: 0, depensesReelles: 0, pourcentageUtilise: 0, budgetRestant: 0 };
    }

    // Essayer plusieurs façons d'accéder aux entries
    let entries = projet.entries;
    if (!entries && projet.expand && projet.expand.entries) {
      entries = projet.expand.entries;
    }
    if (!entries && projet.rubriques) {
      entries = projet.rubriques;
    }
    if (!entries || !Array.isArray(entries)) {
      // Si pas d'entries, essayer de voir s'il y a un budgetTotal direct
      const budgetTotal = Number(projet.budgetTotal) || Number(projet.montantTotal) || 0;
      if (budgetTotal === 0) {
        return { budgetTotal: 0, depensesReelles: 0, pourcentageUtilise: 0, budgetRestant: 0 };
      }
      // Continuer avec le budgetTotal sans entries
      entries = [];
    }

    const budgetTotal = entries.length > 0 
      ? entries.reduce((sum, entry) => sum + (Number(entry.estime) || Number(entry.montant) || 0), 0)
      : Number(projet.budgetTotal) || Number(projet.montantTotal) || 0;
    
    // 🆕 CALCUL AMÉLIORÉ - Utiliser la logique précise de ProjetsPage.js
    const depensesReelles = entries.length > 0
      ? entries.reduce((sum, entry) => sum + getReelForLigneBudgetaire(projet, entry, depensesValideesDG), 0)
      : depensesValideesDG
          .filter(dep => {
            const nomProjetDep = dep.nomProjet || dep.projectNom || dep.projetNom || dep.projet || dep.projetId || "";
            const nomProjetActuel = projet.name || projet.nom || projet.title || "";
            return normalize(nomProjetActuel) === normalize(nomProjetDep) || projet.id === nomProjetDep;
          })
          .reduce((sum, dep) => sum + (Number(dep.montant) || Number(dep.reel) || 0), 0);
    
    const pourcentageUtilise = budgetTotal > 0 ? (depensesReelles / budgetTotal) * 100 : 0;
    const budgetRestant = Math.max(budgetTotal - depensesReelles, 0);

    return {
      budgetTotal,
      depensesReelles,
      pourcentageUtilise,
      budgetRestant
    };
  };

  const getProjectProgressByRubrique = (projet) => {
    if (!projet) return [];

    // Essayer plusieurs façons d'accéder aux entries
    let entries = projet.entries;
    if (!entries && projet.expand && projet.expand.entries) {
      entries = projet.expand.entries;
    }
    if (!entries && projet.rubriques) {
      entries = projet.rubriques;
    }
    if (!entries || !Array.isArray(entries)) return [];

    const progressByRubrique = {};
    
    entries.forEach(entry => {
      const rubrique = entry.rubrique || 'Non spécifié';
      if (!progressByRubrique[rubrique]) {
        progressByRubrique[rubrique] = {
          rubrique,
          budgetEstime: 0,
          depensesReelles: 0,
          sousRubriques: {}
        };
      }
      
      progressByRubrique[rubrique].budgetEstime += Number(entry.estime) || Number(entry.montant) || 0;
      
      // 🆕 CALCUL PRÉCIS - Utiliser getReelForLigneBudgetaire
      const reelEntry = getReelForLigneBudgetaire(projet, entry, depensesValideesDG);
      progressByRubrique[rubrique].depensesReelles += reelEntry;
      
      const sousRubrique = entry.sousRubrique || 'Non spécifié';
      if (!progressByRubrique[rubrique].sousRubriques[sousRubrique]) {
        progressByRubrique[rubrique].sousRubriques[sousRubrique] = {
          budgetEstime: 0,
          depensesReelles: 0
        };
      }
      progressByRubrique[rubrique].sousRubriques[sousRubrique].budgetEstime += Number(entry.estime) || Number(entry.montant) || 0;
      progressByRubrique[rubrique].sousRubriques[sousRubrique].depensesReelles += reelEntry;
    });

    return Object.values(progressByRubrique).map(rubrique => ({
      ...rubrique,
      pourcentageUtilise: rubrique.budgetEstime > 0 ? 
        (rubrique.depensesReelles / rubrique.budgetEstime) * 100 : 0,
      budgetRestant: Math.max(rubrique.budgetEstime - rubrique.depensesReelles, 0)
    }));
  };

  function groupByRubriqueAndSousRubrique(depenses) {
    const grouped = {};
    depenses.forEach(depense => {
      const { rubrique, sousRubrique } = extraireInfosDepense(depense);
      if (!grouped[rubrique]) grouped[rubrique] = {};
      if (!grouped[rubrique][sousRubrique]) grouped[rubrique][sousRubrique] = [];
      grouped[rubrique][sousRubrique].push(depense);
    });
    return grouped;
  }

  function grouperDepensesParProjet(depenses) {
    return depenses.reduce((acc, depense) => {
      const nomProjet = depense.projectNom || depense.nomProjet || "Projet non spécifié";
      if (!acc[nomProjet]) acc[nomProjet] = [];
      acc[nomProjet].push(depense);
      return acc;
    }, {});
  }

  function getJustificatifUrl(depense) {
    if (!depense || !depense.justificatif) return null;
    const justificatifFile = Array.isArray(depense.justificatif) ? depense.justificatif[0] : depense.justificatif;
    const recordId = depense.id;
    const collection = "depensesValideesCG";
    if (justificatifFile.startsWith("http")) return justificatifFile;
    return `${pb.baseUrl}/api/files/${collection}/${recordId}/${justificatifFile}`;
  }

  // Fonctions d'action (gardées identiques mais avec messages améliorés)
  const validerDepenseComptable = async (depense) => {
    setValidatingDepenseIds(prev => new Set(prev).add(depense.id));
    try {
      setLoading(true);

      const formData = new FormData();
      const currentDate = new Date().toISOString();

      formData.append("montant", depense.montant || 0);
      formData.append("created", currentDate);
      formData.append("dateValidationDG", currentDate);
      formData.append("dateValidation", currentDate);
      formData.append("validePar", "DG");
      formData.append("nomProjet", depense.projectNom || depense.nomProjet || "Projet inconnu");
      formData.append("chefChantier", depense.chefChantier || "Non spécifié");
      formData.append("fournisseur", depense.fournisseur || "Inconnu");
      formData.append("description", depense.description || "Aucune description");
      formData.append("rubriques", JSON.stringify(depense.rubriques || []));
      formData.append("depenseOriginaleId", depense.id || "");

      if (depense.justificatif) {
        const fileUrl = pb.files.getUrl(depense, depense.justificatif);
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const file = new File([blob], depense.justificatif, { type: blob.type });
        formData.append("justificatif", file);
      }

      const created = await pb.collection("depensesValideesDG").create(formData);

      if (created) {
        setDepensesValidees(prev => prev.filter(d => d.id !== depense.id));
        setDepensesValideesDG(prev => [...prev, created]);
        setMessage({ type: "success", text: "✅ Dépense validée avec succès" });
        localStorage.setItem('refreshDepensesDG', Date.now());
      }

      await pb.collection("depensesValideesCG").delete(depense.id);

    } catch (error) {
      console.error("Erreur détaillée:", error);
      setMessage({ type: "error", text: "❌ Erreur lors de la validation" });
    } finally {
      setLoading(false);
      setValidatingDepenseIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(depense.id);
        return newSet;
      });
    }
  };

  const openPendingModal = (depense) => {
    setDepenseToPending(depense);
    setShowPendingModal(true);
  };

  const confirmPending = () => {
    const motif = motifRef.current.value.trim();
    if (!motif) {
      alert("Veuillez saisir un motif de mise en attente.");
      return;
    }
    setShowPendingModal(false);
    mettreEnAttenteDepense(depenseToPending, motif);
    setDepenseToPending(null);
  };

 
const mettreEnAttenteDepense = async (depense, motif) => {
  try {
    setLoading(true);
    const formData = new FormData();
    formData.append("montant", depense.montant || 0);
    formData.append("datemea", new Date().toISOString()); // Correction ici
    formData.append("meaPar", "DG"); // Correction ici
    formData.append("nomProjet", depense.projectNom || depense.nomProjet || "Projet inconnu");
    formData.append("chefChantier", depense.chefChantier || "Non spécifié");
    formData.append("fournisseur", depense.fournisseur || "Inconnu");
    formData.append("description", depense.description || "Aucune description");
    formData.append("rubriques", JSON.stringify(depense.rubriques || []));
    formData.append("depenseOriginaleId", depense.id || "");
    formData.append("motif", motif);
    formData.append("status", "en attente");


    if (depense.justificatif) {
      const fileUrl = pb.files.getUrl(depense, depense.justificatif);
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const file = new File([blob], depense.justificatif, { type: blob.type });
      formData.append("justificatif", file);
    }

    const created = await pb.collection("depensemea").create(formData);

    if (created) {
      setDepensesValidees(prev => prev.filter(d => d.id !== depense.id));
      setMessage({ type: "success", text: "⏸ Dépense mise en attente avec succès" });
    }

    await pb.collection("depensesValideesCG").delete(depense.id);

  } catch (error) {
    setMessage({ type: "error", text: "❌ Erreur lors de la mise en attente" });
  } finally {
    setLoading(false);
  }
};


  const validerChargeDG = async (charge) => {
    setValidatingChargeIds(prev => new Set(prev).add(charge.id));
    setLoading(true);
    try {
      await pb.collection('chargesValideesDG').create({
        montant: charge.montant,
        rubrique: charge.rubrique,
        description: charge.description,
        dateValidationDG: new Date().toISOString(),
      });

      await pb.collection('chargesValideesCG').delete(charge.id);

      setChargesStructure(prev => prev.filter(c => c.id !== charge.id));
      setMessage({ type: "success", text: "✅ Charge validée avec succès" });

    } catch (error) {
      setMessage({ type: "error", text: "❌ Erreur lors de la validation de la charge" });
    } finally {
      setLoading(false);
      setValidatingChargeIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(charge.id);
        return newSet;
      });
    }
  };

  const openChargePendingModal = (charge) => {
    setChargeToPending(charge);
    setShowChargePendingModal(true);
  };

  const confirmChargePending = async () => {
    const motif = chargePendingMotifRef.current.value.trim();
    if (!motif) {
      alert("Veuillez saisir un motif de mise en attente.");
      return;
    }
    setLoading(true);
    try {
      await pb.collection('chargemea').create({
        montant: chargeToPending.montant,
        rubrique: chargeToPending.rubrique,
        description: chargeToPending.description,
        datemea: new Date().toISOString(),
        motif: motif,
        meaPar: "DG",
        statut: "en attente",
      });

      await pb.collection('chargesValideesCG').delete(chargeToPending.id);

      setChargesStructure(prev => prev.filter(c => c.id !== chargeToPending.id));
      setMessage({ type: "success", text: "⏸ Charge mise en attente avec succès" });
      setShowChargePendingModal(false);
      setChargeToPending(null);
    } catch (error) {
      setMessage({ type: "error", text: "❌ Erreur lors de la mise en attente de la charge" });
    } finally {
      setLoading(false);
    }
  };

const validerImmobilier = async (immo) => {
  setValidatingImmoIds(prev => new Set(prev).add(immo.id));
  setLoading(true);
  try {
    await pb.collection('immoValideesDG').create({
      montant: immo.montant,
      rubrique: immo.rubrique,
      description: immo.description,
      dateValidationDG: new Date().toISOString(),
    });
    await pb.collection('immoValideesCG').delete(immo.id);
    setImmobilisationsValideesCG(prev => prev.filter(i => i.id !== immo.id));
    setMessage({ type: "success", text: "✅ Immobilisation validée !" });
  } catch (error) {
    setMessage({ type: "error", text: "❌ Erreur validation immobilier" });
  } finally {
    setLoading(false);
    setValidatingImmoIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(immo.id);
      return newSet;
    });
  }
};

const openImmobilierPendingModal = (immo) => {
  setImmobilierToPending(immo);
  setShowImmobilierPendingModal(true);
};

const confirmImmobilierPending = async () => {
  const motif = immobilierPendingMotifRef.current.value.trim();
  if (!motif) {
    alert("Veuillez saisir un motif.");
    return;
  }
  setLoading(true);
  try {
    await pb.collection('immomea').create({
      montant: immobilierToPending.montant,
      rubrique: immobilierToPending.rubrique,
      sousRubrique: immobilierToPending.sousRubrique,
      description: immobilierToPending.description,
      datemea: new Date().toISOString(),
      motif: motif,
      meaPar: "DG",
      statut: "en attente"
    });
    await pb.collection('immoValideesCG').delete(immobilierToPending.id);
    setImmobilisationsValideesCG(prev => prev.filter(i => i.id !== immobilierToPending.id));
    setMessage({ type: "success", text: "⏸ Immobilisation mise en attente !" });
    setShowImmobilierPendingModal(false);
    setImmobilierToPending(null);
  } catch (error) {
    setMessage({ type: "error", text: "❌ Erreur mise en attente immobilier" });
  } finally {
    setLoading(false);
  }
};

  const toggleHistory = (section) => {
    setOpenHistory(prevState => ({
      ...prevState,
      [section]: !prevState[section]
    }));
  };

  const handleLogout = () => {
    pb.authStore.clear();
    navigate('/login', { replace: true });
  };

  // Calculs pour les statistiques
  const totalEnAttente = getPendingExpenses().length;
  const totalValidees = depensesValideesDG.length;
  const totalRejetees = depensesRejeteesDG.length;
  const totalMontantValideDG = depensesValideesDG.reduce((sum, dep) => sum + (Number(dep.montant) || 0), 0);

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 text-gray-900 min-h-screen">
      {/* Header customisé et moderne */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200/50 px-6 lg:px-8 h-16 flex items-center justify-between print:hidden shadow-lg">
        <div className="flex items-center space-x-6 min-w-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div>
              <div className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Dywane
              </div>
              <div className="text-xs text-gray-500 font-medium">Direction Générale</div>
            </div>
          </div>
        </div>
        
        <div className="flex-1 max-w-md mx-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="search"
              placeholder="Rechercher un projet..."
              className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-white/70 backdrop-blur-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 text-sm"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4 min-w-0">
          <div className="flex items-center space-x-3 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full px-4 py-2 shadow-sm">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm">👔</span>
            </div>
            <span className="text-sm font-medium text-gray-700 truncate min-w-0">Directeur Général</span>
          </div>
          <button onClick={() => { setShowLogoutConfirm(true); }} className="text-gray-500 hover:text-gray-700 focus:outline-none p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>

      {/* Sidebar style image */}
      <nav className="fixed top-16 left-0 z-20 bg-slate-800 w-64 h-[calc(100vh-4rem)] flex flex-col text-white text-sm select-none print:hidden">
        <div className="flex-1 px-4 py-6 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            {/* Menu items espacés */}
            <button
              className={`flex items-center space-x-3 w-full rounded px-3 py-3 transition-colors mt-1 ${
                activeTab === "dashboard"
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-slate-700"
              }`}
              onClick={() => setActiveTab("dashboard")}
            >
              <span>🏠</span>
              <span>Tableau de bord</span>
            </button>

            <button
              className={`flex items-center space-x-3 w-full rounded px-3 py-3 transition-colors ${
                activeTab === "etat"
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-slate-700"
              }`}
              onClick={() => setActiveTab("etat")}
            >
              <span>📊</span>
              <span>État</span>
            </button>

            <button
              className={`flex items-center space-x-3 w-full rounded px-3 py-3 transition-colors ${
                activeTab === "charges"
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-slate-700"
              }`}
              onClick={() => setActiveTab("charges")}
            >
              <span>💼</span>
              <span>Charges</span>
            </button>

            <button
              className={`flex items-center space-x-3 w-full rounded px-3 py-3 transition-colors ${
                activeTab === "historique"
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-slate-700"
              }`}
              onClick={() => setActiveTab("historique")}
            >
              <span>🕓</span>
              <span>Historique</span>
            </button>

            <button
              className={`flex items-center space-x-3 w-full rounded px-3 py-3 transition-colors ${
                activeTab === "avancements"
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-slate-700"
              }`}
              onClick={() => setActiveTab("avancements")}
            >
              <span>📊</span>
              <span>Avancements projets</span>
            </button>

            <button
              className={`flex items-center space-x-3 w-full rounded px-3 py-3 transition-colors ${
                activeTab === "immobilier"
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-slate-700"
              }`}
              onClick={() => setActiveTab("immobilier")}
            >
              <span>🏢</span>
              <span>Immobilier</span>
            </button>
          </div>
        </div>
        <div className="border-t border-slate-700 p-4 space-y-2 mt-2">
          <a href="#" className="flex items-center space-x-3 text-gray-300 hover:text-white hover:bg-slate-700 rounded px-3 py-2 transition-colors">
            <span>⚙</span>
            <span>Paramètres</span>
          </a>
          <button onClick={() => {  setShowLogoutConfirm(true); }} className="flex items-center space-x-3 text-orange-400 hover:text-orange-300 hover:bg-slate-700 rounded px-3 py-2 transition-colors w-full text-left">
            <span>🚪</span>
            <span>Déconnexion</span>
          </button>
        </div>


      </nav>

      {/* Main Content */}
      <main className="ml-64 pt-16 min-h-screen p-8">
        {/* Toast notifications */}
        {message && (
          <Toast message={message} onClose={() => setMessage(null)} />
        )}

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {activeTab === "dashboard" ? "Tableau de bord" : 
               activeTab === "charges" ? "Charges de structure" :
               activeTab === "historique" ? "Historique des dépenses" :
               activeTab === "etat" ? "États et rapports" :
               activeTab === "avancements" ? "Avancements des Projets" :
               "Dashboard"}
            </h1>
            <p className="text-gray-600 mt-1">
              {activeTab === "dashboard" ? "Vue d'ensemble des dépenses et validations" : 
               activeTab === "charges" ? "Validation des charges en attente" :
               activeTab === "historique" ? "Historique complet des validations" :
               activeTab === "avancements" ? "Suivi en temps réel de la progression des projets" :
               "Rapports et analyses détaillées"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Dernière mise à jour</p>
            <p className="text-sm font-medium text-gray-900">
              {new Date().toLocaleString('fr-FR')}
            </p>
          </div>
        </div>

        {activeTab === "dashboard" && (
          <div className="space-y-8">
            {/* Cartes de statistiques modernes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="En attente"
                value={totalEnAttente}
                subtitle="dépenses à traiter"
                icon="⏳"
                color="amber"
              />
              <StatCard
                title="Validées"
                value={totalValidees}
                subtitle="dépenses approuvées"
                icon="✅"
                color="emerald"
              />
              <StatCard
                title="Rejetées"
                value={totalRejetees}
                subtitle="dépenses refusées"
                icon="❌"
                color="red"
              />
              <StatCard
                title="Montant validé"
                value={`${(totalMontantValideDG / 1000000).toFixed(1)}M`}
                subtitle="FCFA validés"
                icon="💰"
                color="blue"
              />
            </div>

            {/* Graphiques modernes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Graphique de répartition */}
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <span className="mr-3 p-2 bg-blue-100 rounded-xl">📊</span>
                  Répartition des dépenses
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'En attente', value: totalEnAttente, color: '#f59e0b' },
                        { name: 'Validées', value: totalValidees, color: '#10b981' },
                        { name: 'Rejetées', value: totalRejetees, color: '#ef4444' }
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {COLORS.slice(1, 4).map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Dernières validations */}
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <span className="mr-3 p-2 bg-emerald-100 rounded-xl">📋</span>
                  Dernières validations
                </h3>
                <div className="space-y-4 max-h-80 overflow-y-auto">
                  {depensesValideesDG.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <span className="text-4xl block mb-3">📭</span>
                      <p>Aucune validation récente</p>
                    </div>
                  ) : (
                    depensesValideesDG
                      .sort((a, b) => {
                        const dateA = a.dateValidationDG?.seconds
                          ? new Date(a.dateValidationDG.seconds * 1000)
                          : new Date(a.dateValidationDG || a.dateValidation || a.created);
                        const dateB = b.dateValidationDG?.seconds
                          ? new Date(b.dateValidationDG.seconds * 1000)
                          : new Date(b.dateValidationDG || b.dateValidation || b.created);
                        return dateB - dateA;
                      })
                      .slice(0, 5)
                      .map(dep => {
                        const { rubrique, sousRubrique } = extraireRubrique(dep);
                        return (
                          <div key={dep.id} className="flex items-center justify-between p-4 bg-white/50 rounded-xl hover:bg-white/70 transition-all duration-200 border border-white/30">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{dep.projectNom || dep.nomProjet || "-"}</h4>
                              <p className="text-sm text-gray-600">{rubrique || "-"}</p>
                              <p className="text-xs text-blue-600 font-medium">{sousRubrique || "-"}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-emerald-600">
                                {Number(dep.montant).toLocaleString()} FCFA
                              </p>
                              <p className="text-xs text-gray-400">
                                {dep.dateValidationDG
                                  ? (dep.dateValidationDG.seconds
                                      ? new Date(dep.dateValidationDG.seconds * 1000).toLocaleDateString('fr-FR')
                                      : new Date(dep.dateValidationDG).toLocaleDateString('fr-FR'))
                                  : "-"}
                              </p>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            </div>

            {/* Dépenses à valider par projet */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-white/20">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl">
                    <span className="text-white text-2xl">📝</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Dépenses à valider</h2>
                    <p className="text-gray-600">Organisées par projet</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 px-6 py-3 rounded-xl">
                  <span className="text-blue-800 font-bold text-xl">{totalEnAttente}</span>
                  <span className="text-blue-600 ml-1">en attente</span>
                </div>
              </div>

              <div className="space-y-6">
                {Object.entries(grouperDepensesParProjet(getPendingExpenses())).length === 0 ? (
                  <div className="text-center py-16">
                    <span className="text-6xl block mb-4">🎉</span>
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">Excellent travail !</h3>
                    <p className="text-gray-500 text-lg">Aucune dépense en attente de validation</p>
                  </div>
                ) : (
                  Object.entries(grouperDepensesParProjet(getPendingExpenses())).map(([nomProjet, depensesProjet]) => {
                    const rubriques = groupByRubriqueAndSousRubrique(depensesProjet);
                    const totalProjet = depensesProjet.reduce((sum, dep) => sum + (Number(dep.montant) || 0), 0);
                    
                    return (
                      <div key={nomProjet} className="border border-gray-200/50 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white/50 backdrop-blur-sm">
                        {/* En-tête du projet */}
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                <span className="text-2xl">🏢</span>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold">{nomProjet}</h3>
                                <p className="text-blue-100">{depensesProjet.length} dépense(s) en attente</p>
                              </div>
                            </div>
                            <div className="text-right bg-white/20 backdrop-blur-sm rounded-xl p-4">
                              <p className="text-2xl font-bold">{totalProjet.toLocaleString()} FCFA</p>
                              <p className="text-blue-100">Montant total</p>
                            </div>
                          </div>
                        </div>

                        {/* Tableau des dépenses */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                              <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rubrique</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sous-rubrique</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Montant</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Date demande</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fournisseur</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Justificatif</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {Object.entries(rubriques).map(([rubrique, sousRubriques]) => (
                                <React.Fragment key={rubrique}>
                                  <tr className="bg-gradient-to-r from-blue-50 to-purple-50">
                                    <td colSpan={8} className="px-6 py-3">
                                      <h4 className="font-bold text-blue-900 flex items-center">
                                        <span className="mr-2 p-1 bg-blue-200 rounded-lg">📁</span>
                                        {rubrique}
                                      </h4>
                                    </td>
                                  </tr>
                                  {Object.entries(sousRubriques).map(([sousRubrique, depensesList]) =>
                                    depensesList.map((depense) => {
                                      const { rubrique, sousRubrique, fournisseur, justificatif, dateDemande } = extraireInfosDepense(depense);
                                      return (
                                        <tr key={depense.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                                          <td className="px-6 py-4"></td>
                                          <td className="px-6 py-4">
                                            <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 rounded-full text-sm font-medium">
                                              {sousRubrique}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4">
                                            <ModernButton
                                              onClick={() => setDepenseDescriptionOpen(depense.id)}
                                              variant="primary"
                                              size="sm"
                                              className="text-xs"
                                            >
                                              📖 Lire
                                            </ModernButton>
                                          </td>
                                          <td className="px-6 py-4 text-right">
                                            <span className="font-bold text-gray-900 text-lg">
                                              {Number(depense.montant).toLocaleString()}
                                            </span>
                                            <span className="text-gray-600 ml-1">FCFA</span>
                                          </td>
                                          <td className="px-6 py-4 text-center text-sm text-gray-600">
                                            {dateDemande
                                              ? dateDemande.toLocaleDateString('fr-FR', {
                                                  day: 'numeric',
                                                  month: 'short',
                                                  year: 'numeric'
                                                })
                                              : "-"}
                                          </td>
                                          <td className="px-6 py-4 text-sm text-gray-600">
                                            <span className="px-2 py-1 bg-gray-100 rounded-lg">
                                              {fournisseur}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 text-center">
                                            {justificatif ? (
                                              <ModernButton
                                                onClick={() => {
                                                  setJustificatifUrl(getJustificatifUrl(depense));
                                                  setShowJustificatifModal(true);
                                                }}
                                                variant="secondary"
                                                size="sm"
                                                className="text-xs"
                                              >
                                                📎 Voir
                                              </ModernButton>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </td>
                                          <td className="px-6 py-4">
                                            <div className="flex items-center justify-center space-x-2">
                                              <ModernButton
                                                onClick={() => validerDepenseComptable(depense)}
                                                variant="success"
                                                size="sm"
                                                disabled={loading}
                                                loading={loading}
                                              >
                                                ✓ Valider
                                              </ModernButton>
                                              <ModernButton
                                                onClick={() => openPendingModal(depense)}
                                                variant="warning"
                                                size="sm"
                                              >
                                                ⏸ Attente
                                              </ModernButton>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "avancements" && (
          <div className="space-y-8">
            {/* En-tête avec indicateur temps réel */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl shadow-xl p-8 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                    <span className="text-3xl">📊</span>
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold">Avancements des Projets</h1>
                    <p className="text-emerald-100 mt-2 text-lg">Suivi en temps réel de la progression budgétaire</p>
                    <div className="flex items-center mt-3">
                      <div className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse mr-2"></div>
                      <span className="text-emerald-200 text-sm">Synchronisé avec les dépenses validées</span>
                    </div>
                  </div>
                </div>
                <div className="text-right bg-white/20 backdrop-blur-sm rounded-2xl px-8 py-4">
                  <span className="text-4xl font-bold">{projets.length}</span>
                  <span className="block text-emerald-100 text-lg">projets actifs</span>
                  <div className="flex items-center justify-center mt-2 text-emerald-200 text-xs">
                    <div className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse mr-2"></div>
                    Temps réel
                  </div>
                </div>
              </div>
            </div>

            {/* Vue d'ensemble des projets */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {loadingProjects ? (
                <div className="lg:col-span-3 bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-12 text-center border border-white/20">
                  <div className="flex justify-center items-center mb-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">Chargement des projets...</h3>
                  <p className="text-gray-500">Récupération des données en temps réel</p>
                </div>
              ) : projets.length === 0 ? (
                <div className="lg:col-span-3 bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-12 text-center border border-white/20">
                  <span className="text-6xl block mb-4">📋</span>
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">Aucun projet disponible</h3>
                  <p className="text-gray-500">Les projets créés apparaîtront ici automatiquement</p>
                  <ModernButton 
                    onClick={() => window.location.reload()} 
                    variant="primary" 
                    className="mt-4"
                  >
                    🔄 Actualiser
                  </ModernButton>
                </div>
              ) : (
                <>
                  {/* Liste des projets */}
                  <div className="lg:col-span-1 bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                      <span className="mr-3 p-2 bg-emerald-100 rounded-xl">🏗️</span>
                      Projets ({projets.length})
                    </h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {projets.map((projet) => {
                        const progress = getProjectProgress(projet);
                        const nomProjet = projet.name || projet.nom || projet.title || `Projet ${projet.id?.slice(-4) || 'Sans nom'}`;
                        return (
                          <div
                            key={projet.id}
                            className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                              selectedProject?.id === projet.id
                                ? 'bg-emerald-50 border-emerald-300 shadow-md'
                                : 'bg-white/50 border-gray-200 hover:bg-emerald-50/50 hover:border-emerald-200'
                            }`}
                            onClick={() => setSelectedProject(projet)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900 text-sm truncate pr-2">
                                {nomProjet}
                              </h4>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                progress.pourcentageUtilise > 100 ? 'bg-red-100 text-red-700' :
                                progress.pourcentageUtilise > 80 ? 'bg-orange-100 text-orange-700' :
                                'bg-emerald-100 text-emerald-700'
                              }`}>
                                {progress.pourcentageUtilise.toFixed(1)}%
                              </span>
                            </div>
                            <div className="mb-2">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all duration-500 ${
                                    progress.pourcentageUtilise > 100 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                    progress.pourcentageUtilise > 80 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                                    'bg-gradient-to-r from-emerald-500 to-teal-500'
                                  }`}
                                  style={{ width: `${Math.min(progress.pourcentageUtilise, 100)}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-600">
                              <div className="flex justify-between">
                                <span>Budget: {progress.budgetTotal.toLocaleString()} FCFA</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Utilisé: {progress.depensesReelles.toLocaleString()} FCFA</span>
                              </div>
                              <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                                <span>Créé: {projet.created ? new Date(projet.created).toLocaleDateString('fr-FR') : 'N/A'}</span>
                                {projet.responsableChantier && (
                                  <span>👷 {projet.responsableChantier}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Détails du projet sélectionné */}
                  <div className="lg:col-span-2">
                    {selectedProject ? (
                      <div className="space-y-6">
                        {/* Graphique principal du projet */}
                        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900">
                              Progression: {selectedProject.name || selectedProject.nom || selectedProject.title || `Projet ${selectedProject.id?.slice(-4) || 'Sans nom'}`}
                            </h3>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Responsable</p>
                              <p className="font-semibold text-gray-900">
                                {selectedProject.responsableChantier || "Non spécifié"}
                              </p>
                            </div>
                          </div>
                          
                          {(() => {
                            const progress = getProjectProgress(selectedProject);
                            return (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Graphique en donut */}
                                <div className="flex justify-center">
                                  <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                      <Pie
                                        data={[
                                          { 
                                            name: 'Utilisé', 
                                            value: progress.depensesReelles,
                                            color: progress.pourcentageUtilise > 100 ? '#ef4444' : 
                                                   progress.pourcentageUtilise > 80 ? '#f59e0b' : '#10b981'
                                          },
                                          { 
                                            name: 'Restant', 
                                            value: progress.budgetRestant,
                                            color: '#f3f4f6'
                                          }
                                        ]}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        dataKey="value"
                                      >
                                        <Cell fill={progress.pourcentageUtilise > 100 ? '#ef4444' : 
                                                   progress.pourcentageUtilise > 80 ? '#f59e0b' : '#10b981'} />
                                        <Cell fill="#f3f4f6" />
                                      </Pie>
                                      <Tooltip 
                                        formatter={(value) => [`${Number(value).toLocaleString()} FCFA`, '']}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </div>

                                {/* Résumé financier */}
                                <div className="space-y-4">
                                  <div className={`rounded-xl p-4 border ${
                                    progress.pourcentageUtilise > 100 ? 'bg-gradient-to-r from-red-50 to-red-50 border-red-200' :
                                    progress.pourcentageUtilise > 80 ? 'bg-gradient-to-r from-orange-50 to-orange-50 border-orange-200' :
                                    'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
                                  }`}>
                                    <h4 className={`font-bold mb-3 ${
                                      progress.pourcentageUtilise > 100 ? 'text-red-800' :
                                      progress.pourcentageUtilise > 80 ? 'text-orange-800' :
                                      'text-emerald-800'
                                    }`}>
                                      Résumé Financier
                                      {progress.pourcentageUtilise > 100 && <span className="ml-2">🚨 DÉPASSEMENT</span>}
                                      {progress.pourcentageUtilise > 80 && progress.pourcentageUtilise <= 100 && <span className="ml-2">⚠️ ATTENTION</span>}
                                    </h4>
                                    <div className="space-y-3">
                                      <div className="flex justify-between">
                                        <span className={progress.pourcentageUtilise > 100 ? 'text-red-700' :
                                                       progress.pourcentageUtilise > 80 ? 'text-orange-700' :
                                                       'text-emerald-700'}>Budget Total:</span>
                                        <span className={`font-bold ${
                                          progress.pourcentageUtilise > 100 ? 'text-red-900' :
                                          progress.pourcentageUtilise > 80 ? 'text-orange-900' :
                                          'text-emerald-900'
                                        }`}>
                                          {progress.budgetTotal.toLocaleString()} FCFA
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className={progress.pourcentageUtilise > 100 ? 'text-red-700' :
                                                       progress.pourcentageUtilise > 80 ? 'text-orange-700' :
                                                       'text-emerald-700'}>Dépenses Réelles:</span>
                                        <span className={`font-bold ${
                                          progress.pourcentageUtilise > 100 ? 'text-red-900' :
                                          progress.pourcentageUtilise > 80 ? 'text-orange-900' :
                                          'text-emerald-900'
                                        }`}>
                                          {progress.depensesReelles.toLocaleString()} FCFA
                                        </span>
                                      </div>
                                      <div className={`flex justify-between border-t pt-2 ${
                                        progress.pourcentageUtilise > 100 ? 'border-red-200' :
                                        progress.pourcentageUtilise > 80 ? 'border-orange-200' :
                                        'border-emerald-200'
                                      }`}>
                                        <span className={progress.pourcentageUtilise > 100 ? 'text-red-700' :
                                                       progress.pourcentageUtilise > 80 ? 'text-orange-700' :
                                                       'text-emerald-700'}>Budget Restant:</span>
                                        <span className={`font-bold ${
                                          progress.budgetRestant < 0 ? 'text-red-900' :
                                          progress.pourcentageUtilise > 80 ? 'text-orange-900' :
                                          'text-emerald-900'
                                        }`}>
                                          {progress.budgetRestant.toLocaleString()} FCFA
                                        </span>
                                      </div>
                                      <div className={`flex justify-between rounded-lg p-2 ${
                                        progress.pourcentageUtilise > 100 ? 'bg-red-100' :
                                        progress.pourcentageUtilise > 80 ? 'bg-orange-100' :
                                        'bg-emerald-100'
                                      }`}>
                                        <span className={`font-semibold ${
                                          progress.pourcentageUtilise > 100 ? 'text-red-700' :
                                          progress.pourcentageUtilise > 80 ? 'text-orange-700' :
                                          'text-emerald-700'
                                        }`}>Pourcentage Utilisé:</span>
                                        <span className={`font-bold text-lg ${
                                          progress.pourcentageUtilise > 100 ? 'text-red-900' :
                                          progress.pourcentageUtilise > 80 ? 'text-orange-900' :
                                          'text-emerald-900'
                                        }`}>
                                          {progress.pourcentageUtilise.toFixed(1)}%
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Indicateurs visuels */}
                                  <div className="grid grid-cols-2 gap-3">
                                    <div className={`text-center p-3 rounded-xl ${
                                      progress.pourcentageUtilise > 100 ? 'bg-red-100' :
                                      progress.pourcentageUtilise > 80 ? 'bg-orange-100' :
                                      'bg-emerald-100'
                                    }`}>
                                      <div className={`text-xs mb-1 ${
                                        progress.pourcentageUtilise > 100 ? 'text-red-600' :
                                        progress.pourcentageUtilise > 80 ? 'text-orange-600' :
                                        'text-emerald-600'
                                      }`}>Utilisé</div>
                                      <div className={`font-bold ${
                                        progress.pourcentageUtilise > 100 ? 'text-red-800' :
                                        progress.pourcentageUtilise > 80 ? 'text-orange-800' :
                                        'text-emerald-800'
                                      }`}>
                                        {Math.min(progress.pourcentageUtilise, 100).toFixed(0)}%
                                      </div>
                                    </div>
                                    <div className="text-center p-3 bg-gray-100 rounded-xl">
                                      <div className="text-gray-600 text-xs mb-1">Restant</div>
                                      <div className="text-gray-800 font-bold">
                                        {Math.max(100 - progress.pourcentageUtilise, 0).toFixed(0)}%
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-12 text-center border border-white/20">
                        <span className="text-6xl block mb-4">👈</span>
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">Sélectionnez un projet</h3>
                        <p className="text-gray-500">Cliquez sur un projet à gauche pour voir sa progression détaillée</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === "charges" && (
          <div className="space-y-8">
            {/* En-tête */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-xl p-8 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                    <span className="text-3xl">💼</span>
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold">Charges de structure</h1>
                    <p className="text-purple-100 mt-2 text-lg">Validation des charges et suivi des mises en attente</p>
                  </div>
                </div>
                <div className="text-right bg-white/20 backdrop-blur-sm rounded-2xl px-8 py-4">
                  <span className="text-4xl font-bold">{chargesStructure.length}</span>
                  <span className="block text-purple-100 text-lg">demandes à traiter</span>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20">
                {chargesStructure.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="p-8 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full mb-8">
                      <span className="text-6xl">📋</span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-700 mb-4">Aucune charge à traiter</h3>
                    <p className="text-gray-500 text-xl">Toutes les charges ont été traitées.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="px-8 py-6 text-left text-sm font-bold text-gray-600 uppercase tracking-wider">
                            Rubrique
                          </th>
                          <th className="px-8 py-6 text-left text-sm font-bold text-gray-600 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-8 py-6 text-right text-sm font-bold text-gray-600 uppercase tracking-wider">
                            Montant
                          </th>
                          <th className="px-8 py-6 text-center text-sm font-bold text-gray-600 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {chargesStructure.map((charge, index) => (
                          <tr 
                            key={charge.id} 
                            className="hover:bg-purple-50/50 transition-all duration-300 group"
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            <td className="px-8 py-6">
                              <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-2xl flex items-center justify-center font-bold shadow-lg group-hover:scale-110 transition-transform">
                                  📁
                                </div>
                                <div>
                                  <div className="text-lg font-bold text-gray-900 group-hover:text-purple-700 transition-colors">
                                    {charge.rubrique}
                                  </div>
                                  <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-lg inline-block">
                                    Validé CG: {charge.dateValidationCG ? 
                                      (charge.dateValidationCG.seconds ? 
                                        new Date(charge.dateValidationCG.seconds * 1000).toLocaleDateString('fr-FR')
                                        : new Date(charge.dateValidationCG).toLocaleDateString('fr-FR')
                                      ) 
                                      : 'N/A'
                                    }
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-8 py-6">
                              <div className="text-gray-700 max-w-md leading-relaxed">
                                {charge.description}
                              </div>
                            </td>
                            <td className="px-8 py-6 text-right">
                              <div className="text-2xl font-bold text-gray-900">
                                {Number(charge.montant).toLocaleString()}
                              </div>
                              <div className="text-gray-600">FCFA</div>
                              <div className="text-sm text-gray-500 mt-2">
                                Demandé le: {charge.dateCreation ? 
                                  (charge.dateCreation.seconds ? 
                                    new Date(charge.dateCreation.seconds * 1000) 
                                    : new Date(charge.dateCreation)
                                  ).toLocaleDateString('fr-FR', {
                                    day: 'numeric',
                                    month: 'long', 
                                    year: 'numeric'
                                  })
                                  : 'Non spécifiée'
                                }
                              </div>
                            </td>
                            <td className="px-8 py-6">
                              <div className="flex items-center justify-center space-x-3">
                                <ModernButton
                                  onClick={() => validerChargeDG(charge)}
                                  variant="success"
                                  disabled={loading}
                                  loading={loading}
                                >
                                  ✓ Valider
                                </ModernButton>
                                <ModernButton
                                  onClick={() => openChargePendingModal(charge)}
                                  variant="warning"
                                >
                                  ⏸ Mettre en attente
                                </ModernButton>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
          </div>
        )}

            {activeTab === "historique" && (
              <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl p-10 border border-gray-300">
                <div className="flex items-center space-x-5 mb-10">
                  <div className="p-4 bg-gradient-to-r from-blue-300 to-indigo-400 rounded-2xl shadow-md">
                    <span className="text-3xl">🕓</span>
                  </div>
                  <div>
                    <h1 className="text-4xl font-extrabold text-gray-900">Historique des validations</h1>
                    <p className="text-gray-700 mt-2 text-lg">Cliquez sur une section pour voir les détails</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Section Dépenses */}
                  <div className="border border-gray-200/80 rounded-2xl overflow-hidden transition-all duration-300 ease-in-out">
                    <button
                      onClick={() => toggleHistory('depenses')}
                      className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl">🧾</span>
                        <h2 className="text-xl font-bold text-emerald-800">Historique des Dépenses Projets</h2>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 text-sm font-semibold text-emerald-700 bg-emerald-200 rounded-full">{depensesValideesDG.length}</span>
                        <svg className={`w-6 h-6 text-emerald-700 transform transition-transform duration-300 ${openHistory.depenses ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </button>
                    {openHistory.depenses && (
                      <div className="p-6 bg-white">
                        {Object.entries(grouperDepensesParProjet(depensesValideesDG)).length > 0 ? (
                          <div className="space-y-8">
                            {Object.entries(grouperDepensesParProjet(depensesValideesDG)).map(([nomProjet, depensesProjet]) => {
                              const rubriques = groupByRubriqueAndSousRubrique(depensesProjet);
                              const totalProjet = depensesProjet.reduce((sum, dep) => sum + (Number(dep.montant) || 0), 0);
                              return (
                                <div key={nomProjet} className="border border-gray-200 rounded-xl overflow-hidden">
                                  <div className="bg-gray-100 p-4 flex justify-between items-center">
                                    <h3 className="font-bold text-gray-800">{nomProjet}</h3>
                                    <span className="font-bold text-emerald-800">{totalProjet.toLocaleString()} FCFA</span>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rubrique</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sous-rubrique</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date validation</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fournisseur</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {Object.entries(rubriques).map(([rubrique, sousRubriques]) => (
                                          <React.Fragment key={rubrique}>
                                            <tr className="bg-gray-100"><td className="px-6 py-3 font-bold text-gray-700" colSpan={6}>{rubrique}</td></tr>
                                            {Object.entries(sousRubriques).map(([sousRubrique, depensesList]) =>
                                              depensesList.map((depense) => {
                                                const { fournisseur } = extraireInfosDepense(depense);
                                                const dateValidation = depense.dateValidationDG || depense.dateValidation || depense.created;
                                                return (
                                                  <tr key={depense.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4"></td>
                                                    <td className="px-6 py-4"><span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-semibold">{sousRubrique}</span></td>
                                                    <td className="px-6 py-4"><ModernButton onClick={() => setHistoriqueDescriptionOpen(depense.id)} variant="secondary" size="sm" className="text-xs">📖 Lire</ModernButton></td>
                                                    <td className="px-6 py-4 text-right font-medium text-gray-900">{Number(depense.montant).toLocaleString()} FCFA</td>
                                                    <td className="px-6 py-4 text-center text-sm text-gray-500">{dateValidation ? new Date(dateValidation).toLocaleDateString('fr-FR') : "-"}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">{fournisseur}</td>
                                                  </tr>
                                                );
                                              })
                                            )}
                                          </React.Fragment>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 italic text-center py-4">Aucune dépense de projet validée.</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Section Charges */}
                  <div className="border border-gray-200/80 rounded-2xl overflow-hidden transition-all duration-300 ease-in-out">
                    <button
                      onClick={() => toggleHistory('charges')}
                      className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl">💼</span>
                        <h2 className="text-xl font-bold text-purple-800">Historique des Charges de Structure</h2>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 text-sm font-semibold text-purple-700 bg-purple-200 rounded-full">{chargesValideesDG.length}</span>
                        <svg className={`w-6 h-6 text-purple-700 transform transition-transform duration-300 ${openHistory.charges ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </button>
                    {openHistory.charges && (
                      <div className="p-6 bg-white">
                        {chargesValideesDG.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rubrique</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date validation</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {chargesValideesDG.map((charge) => {
                                  const dateValidation = charge.dateValidationDG || charge.created;
                                  return (
                                    <tr key={charge.id} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 font-semibold text-gray-800">{charge.rubrique}</td>
                                      <td className="px-6 py-4 text-sm text-gray-600">{charge.description}</td>
                                      <td className="px-6 py-4 text-right font-medium text-gray-900">{Number(charge.montant).toLocaleString()} FCFA</td>
                                      <td className="px-6 py-4 text-center text-sm text-gray-500">{dateValidation ? new Date(dateValidation).toLocaleDateString('fr-FR') : "-"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 italic text-center py-4">Aucune charge de structure validée.</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Section Immobilisations */}
                  <div className="border border-gray-200/80 rounded-2xl overflow-hidden transition-all duration-300 ease-in-out">
                    <button
                      onClick={() => toggleHistory('immo')}
                      className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl">🏢</span>
                        <h2 className="text-xl font-bold text-amber-800">Historique des Immobilisations</h2>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 text-sm font-semibold text-amber-700 bg-amber-200 rounded-full">{immoValideesDG.length}</span>
                        <svg className={`w-6 h-6 text-amber-700 transform transition-transform duration-300 ${openHistory.immo ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </button>
                    {openHistory.immo && (
                      <div className="p-6 bg-white">
                        {immoValideesDG.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rubrique</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sous-rubrique</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date validation</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {immoValideesDG.map((immo) => {
                                  const dateValidation = immo.dateValidationDG || immo.created;
                                  return (
                                    <tr key={immo.id} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 font-semibold text-gray-800">{immo.rubrique?.rubrique || '-'}</td>
                                      <td className="px-6 py-4 text-sm text-gray-600">{immo.rubrique?.sousRubrique || '-'}</td>
                                      <td className="px-6 py-4 text-sm text-gray-600">{immo.description}</td>
                                      <td className="px-6 py-4 text-right font-medium text-gray-900">{Number(immo.montant).toLocaleString()} FCFA</td>
                                      <td className="px-6 py-4 text-center text-sm text-gray-500">{dateValidation ? new Date(dateValidation).toLocaleDateString('fr-FR') : "-"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 italic text-center py-4">Aucune immobilisation validée.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}


                    {activeTab === "etat" && (
                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-white/20">
                        <div className="flex items-center justify-between mb-8">
                          <div className="flex items-center space-x-4">
                            <div className="p-3 bg-gradient-to-r from-orange-100 to-orange-200 rounded-xl">
                              <span className="text-2xl">📈</span>
                            </div>
                            <div>
                              <h1 className="text-3xl font-bold text-gray-900">Rapports et états</h1>
                              <p className="text-gray-600">Analyses et rapports détaillés</p>
                            </div>
                          </div>
                          <ModernButton onClick={() => window.print()}>
                            🖨️ Imprimer
                          </ModernButton>
                        </div>

                        <FiltreDepenses
                          filtreDateDebut={filtreDateDebut}
                          setFiltreDateDebut={setFiltreDateDebut}
                          filtreDateFin={filtreDateFin}
                          setFiltreDateFin={setFiltreDateFin}
                        />
                      </div>
                    )}


            {activeTab === "immobilier" && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white mb-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                        <span className="text-3xl">🏢</span>
                      </div>
                      <div>
                        <h1 className="text-3xl font-bold">Gestion des Immobilisations</h1>
                        <p className="text-blue-100 mt-2 text-lg">Validées CG & mises en attente DG</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Contenu des onglets */}
                <div className="flex gap-6 flex-wrap">
                    {immobilisationsValideesCG.length === 0 ? (
                      <div className="text-center py-16 w-full">
                        <span className="text-6xl block mb-4">📋</span>
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">Aucune immobilisation à traiter</h3>
                        <p className="text-gray-500 text-lg">Toutes les immobilisations ont été traitées</p>
                      </div>
                    ) : (
                   
                        immobilisationsValideesCG.map((immo) => {
                          let rubrique = "-";
                          let sousRubrique = "-";
                          // Correction : extraction des valeurs si rubrique est un objet
                          if (typeof immo.rubrique === "object" && immo.rubrique !== null) {
                            rubrique = immo.rubrique.rubrique || immo.rubrique.nom || "-";
                            sousRubrique = immo.rubrique.sousRubrique || "-";
                          } else if (typeof immo.rubrique === "string") {
                            rubrique = immo.rubrique;
                            sousRubrique = immo.sousRubrique || "-";
                          }
                          // Correction : forcer en string si jamais c'est un objet
                          if (typeof rubrique === "object") rubrique = JSON.stringify(rubrique);
                          if (typeof sousRubrique === "object") sousRubrique = JSON.stringify(sousRubrique);

                          const dateVal = immo.dateValidationCG
                            ? (immo.dateValidationCG.seconds
                                ? new Date(immo.dateValidationCG.seconds * 1000)
                                : new Date(immo.dateValidationCG))
                            : null;
                          return (
                            <div key={immo.id} className="bg-white rounded-xl shadow p-6 mb-4 w-[350px] border-l-4 border-blue-400">
                              <div className="font-bold text-blue-900 mb-2">{rubrique}</div>
                              <div className="text-xs text-purple-700 mb-1">{sousRubrique}</div>
                              <div className="text-xs text-gray-500 mb-2">
                                Validé CG: {dateVal ? dateVal.toLocaleDateString('fr-FR') : 'N/A'}
                              </div>
                              <div className="mb-2">
                                <span className="font-semibold text-gray-800">Description: </span>
                                {immo.description?.length > 80 ? (
                                  <span title={immo.description}>{immo.description.slice(0, 80)}…</span>
                                ) : (
                                  immo.description || "-"
                                )}
                              </div>
                              <div className="font-bold text-blue-700 mb-2">
                                Montant : {Number(immo.montant).toLocaleString()} FCFA
                              </div>
                              <div className="flex gap-2 mt-3">
                                <ModernButton
                                  onClick={() => validerImmobilier(immo)}
                                  variant="success"
                                  size="sm"
                                >
                                  Valider
                                </ModernButton>
                                <ModernButton
                                  onClick={() => openImmobilierPendingModal(immo)}
                                  variant="warning"
                                  size="sm"
                                >
                                  En attente
                                </ModernButton>
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                
              </div>
            )}
                </main>

      {/* Modales modernes */}
      
      {/* Modale de mise en attente */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 transform animate-scale-in border border-white/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-amber-100 rounded-2xl">
                <span className="text-2xl">⏸</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Mise en attente</h2>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Motif de la mise en attente
              </label>
              <textarea
                ref={motifRef}
                className="w-full border border-gray-300 rounded-2xl p-4 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none backdrop-blur-sm"
                rows={4}
                placeholder="Expliquez le motif de la mise en attente..."
                autoFocus
              />
            </div>
            
            <div className="flex space-x-3">
              <ModernButton
                onClick={() => {
                  setShowPendingModal(false);
                  setDepenseToPending(null);
                }}
                variant="secondary"
                className="flex-1"
              >
                Annuler
              </ModernButton>
              <ModernButton
                onClick={confirmPending}
                variant="warning"
                className="flex-1"
              >
                Confirmer
              </ModernButton>
            </div>
          </div>
        </div>
      )}

      {/* Modale de mise en attente des charges */}
      {showChargePendingModal && chargeToPending && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 transform animate-scale-in border border-white/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-amber-100 rounded-2xl">
                <span className="text-2xl">⏸</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Mise en attente de la charge</h2>
            </div>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Rubrique</p>
              <p className="font-semibold text-gray-900">{chargeToPending.rubrique}</p>
              <p className="text-sm text-gray-600 mb-1 mt-2">Montant</p>
              <p className="font-bold text-gray-900">{Number(chargeToPending.montant).toLocaleString()} FCFA</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Motif de la mise en attente *
              </label>
              <textarea
                ref={chargePendingMotifRef}
                className="w-full border border-gray-300 rounded-2xl p-4 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none backdrop-blur-sm"
                rows={4}
                placeholder="Expliquez le motif de la mise en attente..."
                autoFocus
              />
            </div>
            
            <div className="flex space-x-3">
              <ModernButton
                onClick={() => {
                  setShowChargePendingModal(false);
                  setChargeToPending(null);
                }}
                variant="secondary"
                className="flex-1"
              >
                Annuler
              </ModernButton>
              <ModernButton
                onClick={confirmChargePending}
                variant="warning"
                loading={loading}
                className="flex-1"
              >
                Confirmer la mise en attente
              </ModernButton>
            </div>
          </div>
        </div>
      )}

      {/* Modale de description */}
      {depenseDescriptionOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 w-full max-w-2xl mx-4 transform animate-scale-in border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Description de la dépense</h2>
              <button
                onClick={() => setDepenseDescriptionOpen(null)}
                className="text-gray-400 hover:text-gray-600 text-3xl p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 max-h-96 overflow-y-auto">
              <div className="whitespace-pre-line text-gray-800 leading-relaxed">
                {depensesValidees.find(d => d.id === depenseDescriptionOpen)?.description || "Aucune description disponible"}
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <ModernButton onClick={() => setDepenseDescriptionOpen(null)}>
                Fermer
              </ModernButton>
            </div>
          </div>
        </div>
      )}

      {/* Modale de description historique */}
      {historiqueDescriptionOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 w-full max-w-2xl mx-4 transform animate-scale-in border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Description de la dépense validée</h2>
              <button
                onClick={() => setHistoriqueDescriptionOpen(null)}
                className="text-gray-400 hover:text-gray-600 text-3xl p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 max-h-96 overflow-y-auto">
              <div className="whitespace-pre-line text-gray-800 leading-relaxed">
                {depensesValideesDG.find(d => d.id === historiqueDescriptionOpen)?.description || "Aucune description disponible"}
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <ModernButton onClick={() => setHistoriqueDescriptionOpen(null)}>
                Fermer
              </ModernButton>
            </div>
          </div>
        </div>
      )}

      {/* Modale justificatif */}
      {showJustificatifModal && justificatifUrl && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 w-full max-w-4xl mx-4 transform animate-scale-in max-h-[90vh] overflow-hidden border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Justificatif</h2>
              <button
                onClick={() => setShowJustificatifModal(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-6 flex justify-center">
              {/\.(pdf)$/i.test(justificatifUrl) ? (
                <iframe
                  src={justificatifUrl}
                  title="Justificatif PDF"
                  className="w-full h-96 border rounded-2xl"
                />
              ) : (
                <img
                  src={justificatifUrl}
                  alt="Justificatif"
                  className="max-w-full max-h-96 rounded-2xl shadow-lg"
                />
              )}
            </div>
            
            <div className="flex justify-end">
              <ModernButton
                onClick={() => window.open(justificatifUrl, '_blank')}
              >
                🔗 Ouvrir dans un nouvel onglet
              </ModernButton>
            </div>
          </div>
        </div>
      )}

      {showImmobilierPendingModal && immobilierToPending && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 transform animate-scale-in border border-white/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-amber-100 rounded-2xl">
                <span className="text-2xl">⏸</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Mise en attente de l'immobilier</h2>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Motif de la mise en attente *
              </label>
              <textarea
                ref={immobilierPendingMotifRef}
                className="w-full border border-gray-300 rounded-2xl p-4 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none backdrop-blur-sm"
                rows={4}
                placeholder="Expliquez le motif de la mise en attente..."
                autoFocus
              />
            </div>
            <div className="flex space-x-3">
              <ModernButton
                onClick={() => {
                  setShowImmobilierPendingModal(false);
                  setImmobilierToPending(null);
                }}
                variant="secondary"
                className="flex-1"
              >
                Annuler
              </ModernButton>
              <ModernButton
                onClick={confirmImmobilierPending}
                variant="warning"
                loading={loading}
                className="flex-1"
              >
                Confirmer la mise en attente
              </ModernButton>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation de déconnexion */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 transform animate-scale-in border border-white/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-red-100 rounded-2xl">
                <span className="text-2xl">🚪</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Déconnexion</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              Êtes-vous sûr de vouloir vous déconnecter ?
            </p>
            
            <div className="flex space-x-3">
              <ModernButton
                onClick={() => setShowLogoutConfirm(false)}
                variant="secondary"
                className="flex-1"
              >
                Annuler
              </ModernButton>
              <ModernButton
                onClick={handleLogout}
                variant="danger"
                className="flex-1"
              >
                Oui, déconnecter
              </ModernButton>
            </div>
          </div>
        </div>
      )}

      {/* Styles personnalisés */}
      <style jsx>{`
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
